import { ChartOptions } from 'chart.js';
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears
} from 'date-fns';
import dayjs from 'dayjs';

const getTimeUnit = (timeUnit: string): string => {
  if (timeUnit === 'day') return 'MMM dd';
  if (timeUnit === 'week') return 'MMM yyyy';
  if (timeUnit === 'quarter') return "'Q'Q yyyy";
  return 'yyyy';
};

export const determineTimeUnit = (startDate: Date, endDate: Date): string => {
  const daysDifference = differenceInDays(endDate, startDate);
  const monthsDifference = differenceInMonths(endDate, startDate);
  const yearsDifference = differenceInYears(endDate, startDate);

  if (yearsDifference >= 1) return 'year';
  if (monthsDifference >= 12) return 'quarter';
  if (monthsDifference >= 6) return 'month';
  if (daysDifference >= 31) return 'week';
  return 'day';
};

export const generateChartOptions = (
  xAxisLabel: string | undefined,
  yAxisLabel: string | undefined,
  maxYValue: number,
  timeUnit: string,
  chartPadding: { padding: { top: number; left: number; right: number } },
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | undefined,
  isSmallScreen?: boolean,
  hideLegend?: boolean
): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false as any,
  scales: {
    x: {
      type: 'timeseries',
      time: {
        unit: timeUnit as any,
        tooltipFormat: getTimeUnit(timeUnit),
        displayFormats: {
          day: 'MMM dd',
          week: 'MMM dd',
          month: 'MMM yyyy',
          quarter: "'Q'Q yyyy",
          year: 'yyyy'
        },
        parser: 'YYYY-MM-DD'
      },
      adapters: {
        date: {
          zone: 'local'
        }
      },
      title: {
        display: true,
        text: xAxisLabel
      },
      offset: false,
      ticks: {
        maxTicksLimit: 15,
        autoSkip: true,
        maxRotation: 45,
        minRotation: 0
      }
    },
    y: {
      beginAtZero: true,
      suggestedMax: maxYValue * 1.1,
      title: {
        display: true,
        text: yAxisLabel
      },
      ticks: {
        maxTicksLimit: 10,
        callback: (tickValue: string | number) =>
          Math.floor(
            typeof tickValue === 'string' ? parseInt(tickValue, 10) : tickValue
          )
      }
    }
  },
  plugins: {
    legend: {
      display: !isSmallScreen && !hideLegend,
      position: 'right' as const,
      align: 'start' as const,
      title: {
        padding: 20
      },
      labels: {
        usePointStyle: true,
        pointStyle: 'circle' as const,
        padding: 25,
        boxWidth: 25,
        boxHeight: 10,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      position: 'nearest',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      titleColor: '#000',
      titleFont: {
        weight: 'bold'
      },
      bodyColor: '#000',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      padding: 10,
      callbacks: {
        title: (tooltipItems) => {
          const timestamp = tooltipItems[0].parsed.x;
          const date = dayjs(timestamp);

          if (timeUnit === 'quarter') {
            const quarter = Math.floor(date.month() / 3) + 1;
            return `Q${quarter} ${date.year()}`;
          }

          return date.format(
            timeUnit === 'year' || timeUnit === 'month'
              ? 'MMM D, YYYY'
              : 'MMM D'
          );
        },

        label: (tooltipItem) => {
          const {
            parsed: { y: currentValue },
            dataIndex
          } = tooltipItem;

          const visibleDatasets = tooltipItem.chart.data.datasets.filter(
            (dataset, index) => !tooltipItem.chart.getDatasetMeta(index).hidden
          );

          const totalAtPoint = visibleDatasets.reduce((sum, dataset) => {
            const dataPoint = dataset.data[dataIndex] as
              | { x: number; y: number }
              | undefined;
            return sum + (dataPoint?.y ?? 0);
          }, 0);

          const percentage =
            totalAtPoint > 0
              ? ((currentValue / totalAtPoint) * 100).toFixed(1)
              : '0.0';

          return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue} (${percentage}%)`;
        }
      }
    }
  },
  hover: {
    mode: 'nearest' as const,
    intersect: false
  },
  layout: chartPadding
});
